import { AuctionRequest, FileParams, Patient } from "core/types";
import FileUploadStatusPanel from "dsl/ecosystems/PatientFiles/FileUploadStatusPanel";
import { useFilesFeatures } from "dsl/ecosystems/PatientFiles/useFilesFeature";
import {
  Dispatch,
  ReactNode,
  SetStateAction,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";

type PatientFilesContextType = Omit<
  ReturnType<typeof useFilesFeatures>,
  "setUploadStates" | "uploadStates"
> & {
  setAuctionRequest: Dispatch<SetStateAction<AuctionRequest | undefined>>;
  setExternalId: Dispatch<SetStateAction<string | undefined>>;
  setParams: Dispatch<SetStateAction<FileParams | undefined>>;
  setDisableFileShare: Dispatch<SetStateAction<boolean>>;
};

const PatientFilesContext = createContext<PatientFilesContextType | undefined>(
  undefined,
);

export function PatientFilesProvider({ children }: { children: ReactNode }) {
  const [params, setParams] = useState<FileParams>();
  const [externalId, setExternalId] = useState<Patient["external_id"]>();
  const [auctionRequest, setAuctionRequest] = useState<AuctionRequest>();
  const [disableFileShare, setDisableFileShare] = useState<boolean>(false);
  const { setUploadStates, uploadStates, ...filesFeature } = useFilesFeatures({
    auctionRequest,
    disableFileShare,
    externalId: externalId,
    params: params as FileParams,
  });

  const memoizedSetAuctionRequest = useCallback(setAuctionRequest, []);
  const memoizedSetDisableFileShare = useCallback(setDisableFileShare, []);
  const memoizedSetExternalId = useCallback(setExternalId, []);
  const memoizedSetParams = useCallback(setParams, []);

  return (
    <PatientFilesContext.Provider
      value={{
        ...filesFeature,
        setAuctionRequest: memoizedSetAuctionRequest,
        setDisableFileShare: memoizedSetDisableFileShare,
        setExternalId: memoizedSetExternalId,
        setParams: memoizedSetParams,
      }}
    >
      {children}
      <FileUploadStatusPanel
        fileUploadStates={uploadStates}
        setFileUploadStates={setUploadStates}
      />
    </PatientFilesContext.Provider>
  );
}

export function usePatientFilesContext({
  auctionRequest,
  disableFileShare,
  externalId,
  params,
}: {
  auctionRequest?: AuctionRequest;
  externalId: Patient["external_id"];
  params: FileParams;
  disableFileShare?: boolean;
}) {
  const context = useContext(PatientFilesContext);
  if (!context) {
    throw new Error(
      "usePatientFilesContext must be used within a PatientFilesProvider",
    );
  }

  const { setAuctionRequest, setDisableFileShare, setExternalId, setParams } =
    context;

  useEffect(() => {
    setAuctionRequest(auctionRequest);
    setParams(params);
    setExternalId(externalId);
    setDisableFileShare(disableFileShare ?? false);
  }, [
    auctionRequest,
    disableFileShare,
    externalId,
    params,
    setAuctionRequest,
    setDisableFileShare,
    setExternalId,
    setParams,
  ]);

  return context;
}
